import React, { useEffect, useState, useMemo, useRef, useImperativeHandle } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  format,
  endOfMonth
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { webinarApi } from '../../services';
import { getTimezoneOptions } from './slotBooking/slotUtils';
import { SlotPickerContainer } from './slotBooking/SlotBooking.styles';

const DatePicker = React.forwardRef(
  (props: any, ref) => {
    const [searchParams] = useSearchParams();
    const field_name = props.data?.field_name;
    const webinarId = searchParams.get('webinarId') || "697239b986ed68ed35aee7d3";
    // const startHour = props.data?.startHour !== undefined ? parseInt(props.data.startHour) : 9;
    // const endHour = props.data?.endHour !== undefined ? parseInt(props.data.endHour) : 21;
    // const slotInterval = props.data?.slotInterval !== undefined ? parseInt(props.data.slotInterval) : 30;

    const [selectedDate, setSelectedDate] =
      useState<Date | null>(null);
    const [selectedTime, setSelectedTime] =
      useState<string>('');
    const [selectedSlotIso, setSelectedSlotIso] =
      useState<string>('');
    const [displaySlots, setDisplaySlots] = useState<any>([]);
    const [availableSlots, setAvailableSlots] = useState<string[]>([]);
    const [error, setError] = useState<string>('');
    const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
    const [selectedTimezone, setSelectedTimezone] = useState<string>(
      Intl.DateTimeFormat().resolvedOptions().timeZone
    );
    const hiddenInputRef = useRef<HTMLInputElement>(null);

    const timezones = useMemo(getTimezoneOptions, []);

    useImperativeHandle(ref, () => hiddenInputRef.current);

    const fetchAvailableSlots = async (monthDate: Date = new Date()) => {
      try {
        setError('');

        const lastDayOfMonth = endOfMonth(monthDate);
        const endAtDate = format(lastDayOfMonth, 'yyyy-MM-dd');

        const filter = {
          endAt: endAtDate
        };

        const response = await webinarApi.getAvailableSlots(webinarId, filter);
        const slots = response.schedule || [];
        setAvailableSlots(slots);
      } catch (err: any) {
        console.error('Error fetching slots:', err);
        setError('Failed to load available slots');
        setAvailableSlots([]);
      }
    };

    useEffect(() => {
      if (webinarId) {
        fetchAvailableSlots(currentMonth);
      }
    }, [webinarId, currentMonth]);

    // const generateTimeSlots = (date: Date) => {
    //   const slots = [];
    //   const now = new Date();

    //   let startTime;
    //   if (isToday(date)) {
    //     const minutes = now.getMinutes();
    //     const roundedMinutes = Math.ceil(minutes / slotInterval) * slotInterval;
    //     startTime = new Date(now);
    //     startTime.setMinutes(roundedMinutes, 0, 0);

    //     const configuredStart = new Date(date);
    //     configuredStart.setHours(startHour, 0, 0, 0);

    //     if (startTime < configuredStart) {
    //       startTime = configuredStart;
    //     }
    //   } else {
    //     startTime = new Date(date);
    //     startTime.setHours(startHour, 0, 0, 0);
    //   }

    //   const endTime = new Date(date);
    //   endTime.setHours(endHour, 0, 0, 0);

    //   let currentSlot = new Date(startTime);

    //   while (currentSlot <= endTime) {
    //     const timeString = format(currentSlot, 'HH:mm');
    //     const displayString = format(
    //       currentSlot,
    //       'h:mma',
    //     ).toLowerCase();

    //     slots.push({
    //       time: timeString,
    //       display: displayString,
    //     });

    //     currentSlot = addMinutes(currentSlot, slotInterval);
    //   }

    //   return slots;
    // };

    const getSlotsForDate = (date: Date, timezone: string) => {
      const targetDateString = formatInTimeZone(date, timezone, 'yyyy-MM-dd');

      const filteredSlots = availableSlots.filter((slotIso: string) => {
        try {
          const slotDateInTz = formatInTimeZone(new Date(slotIso), timezone, 'yyyy-MM-dd');
          return slotDateInTz === targetDateString;
        } catch (e) {
          return false;
        }
      });

      const slots = filteredSlots.map((slotIso: string) => {
        const time = formatInTimeZone(new Date(slotIso), timezone, 'HH:mm');
        const display = formatInTimeZone(new Date(slotIso), timezone, 'h:mmaaa').toLowerCase();

        return {
          time,
          display,
          iso: slotIso
        };
      });

      return slots.sort((a, b) => a.time.localeCompare(b.time));
    };

    const hasAvailableSlots = (date: Date) => {
      const targetDateString = format(date, 'yyyy-MM-dd');
      return availableSlots.some((slotIso: string) => {
        try {
          const slotDateInTz = formatInTimeZone(new Date(slotIso), selectedTimezone, 'yyyy-MM-dd');
          return slotDateInTz === targetDateString;
        } catch (e) {
          return false;
        }
      });
    };

    useEffect(() => {
      if (selectedDate) {
        const slots = getSlotsForDate(selectedDate, selectedTimezone);
        setDisplaySlots(slots);
      } else {
        setDisplaySlots([]);
      }
    }, [selectedDate, selectedTimezone, availableSlots]);

    const handleDateChange = (date: Date | null) => {
      setSelectedDate(date);
      setSelectedTime('');
      setSelectedSlotIso('');
    };

    const handleTimeSelect = (slot: any) => {
      setSelectedTime(slot.time);
      setSelectedSlotIso(slot.iso);
      
      if (props.onChange && field_name) {
        props.onChange({
          [field_name]: slot.iso
        });
      }
    };

    const handleTimezoneChange = (event: any) => {
      setSelectedTimezone(event.target.value);
    };

    const handleMonthChange = (newMonth: Date | null) => {
      if (newMonth) {
        setCurrentMonth(newMonth);
      }
    };


    if (!webinarId) {
      return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <SlotPickerContainer ref={ref as any} className="datepicker-wrapper">
            <div className="loader-container">
              <div className="loader-text" style={{ color: '#d32f2f' }}>
                No webinar ID provided. Please add ?webinarId=YOUR_ID to the URL.
              </div>
            </div>
          </SlotPickerContainer>
        </LocalizationProvider>
      );
    }

    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <SlotPickerContainer
          ref={ref as any}
          className="datepicker-wrapper"
        >
          <div className="datepicker-grid">
            <div className="datepicker">
              <h2 className="datepicker-title">
                Select a Day
              </h2>
              <DateCalendar
                value={selectedDate}
                onChange={handleDateChange}
                onMonthChange={handleMonthChange}
                minDate={new Date()}
                disablePast
                views={['day']}
                className="datepicker-calendar"
                slotProps={{
                  day: (dayProps) => ({
                    className: hasAvailableSlots(dayProps.day) ? 'has-slots' : '',
                  }),
                }}
              />
            </div>

            <div className="datepicker time-section">
              {selectedDate ? (
                <>
                  <h2 className="datepicker-title">
                    Select a Time
                  </h2>
                  <div style={{ marginBottom: '15px' }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="timezone-select-label">Timezone</InputLabel>
                      <Select
                        labelId="timezone-select-label"
                        value={selectedTimezone}
                        label="Timezone"
                        onChange={handleTimezoneChange}
                        sx={{ borderRadius: '20px', fontSize: '0.85rem' }}
                      >
                        {timezones.map(tz => (
                          <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  <div className="time-slots-container">
                    {error ? (
                      <div className="center-box" style={{ color: '#d32f2f' }}>
                        <span>{error}</span>
                      </div>
                    ) : displaySlots.length > 0 ? (
                      <div className="time-slots-grid">
                        {displaySlots.map((slot: any) => (
                          <button
                            key={slot.time}
                            type="button"
                            className={`time-slot-button ${selectedTime === slot.time ? 'selected' : ''}`}
                            onClick={() =>
                              handleTimeSelect(slot)
                            }
                          >
                            {slot.display}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="empty-message">
                        No available time slots
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="center-box">
                  <span>Please select a date first</span>
                </div>
              )}
            </div>
          </div>
          <input
            ref={hiddenInputRef}
            type="hidden"
            name={field_name}
            value={selectedSlotIso || ''}
          />
        </SlotPickerContainer>
      </LocalizationProvider>
    );
  },
);

DatePicker.displayName = 'DatePicker';

export default DatePicker;
